import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { plural } from '../../../../../shared/helpers/plural';
import { useAddToast } from '../../../../contexts/ToastContext';
import * as ProjectsV2Store from '../../../../redux/projectsV2';
import { BUTTON_SIZE, BUTTON_TYPE, ButtonGroup, ErrorAlert, InlineLoaderButton } from '../../../generic';
import Button from '../../../generic/Button';
import LabeledTextInput from '../../../generic/Forms/LabeledTextInput';
import type { Project } from '../../../../../shared/projectsV2/response';

import ProjectListComponent from './ProjectListComponent';

import './DashboardV2Modal.less';

type RemoveTeamModalProps = {
  show: boolean;
  teamName: string;
  selectedProjects: Project[] | 'all';
  selectedProjectsCount: number;
  onHide: () => void;
};

export const RemoveTeamModal: VoidFunctionComponent<RemoveTeamModalProps> = ({
  show,
  selectedProjects,
  selectedProjectsCount,
  teamName,
  onHide,
}) => {
  const dispatch = useDispatch();
  const { loading: isFetchingProjects } = useSelector(ProjectsV2Store.selectors.getAsyncStatusFetchProjects);
  const { loading: isRemovingTeam, errors } = useSelector(
    ProjectsV2Store.selectors.getAsyncStatusUpdateProjects,
  );

  const [bulkDeleteConfirmationText, setBulkDeleteConfirmationText] = useState('');

  const isLoading = isRemovingTeam && isFetchingProjects;
  const isRemoveButtonDisabled = isLoading || (selectedProjects === 'all' && bulkDeleteConfirmationText !== 'DELETE');

  const addToast = useAddToast();

  const onEnter = () => {
    setBulkDeleteConfirmationText('');
  };

  return (
    <Modal
      backdrop="static"
      animation
      show={show}
      onEnter={onEnter}
      onHide={onHide}
      onEscapeKeyDown={onHide}
      className="projects-dashboard-v2-modal"
    >
      <Modal.Header bsClass="projects-dashboard-v2-modal__header">
        <Modal.Title bsClass="projects-dashboard-v2-modal__header__title">
          Remove projects from team?
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="projects-dashboard-v2-modal__body">
        <div className="projects-dashboard-v2-modal__body__message">
          <span className="projects-dashboard-v2-modal__body__message__strong">
            {`${selectedProjectsCount.toLocaleString()} ${plural(selectedProjectsCount, 'project')}`}
          </span>
          &nbsp;will be removed from&nbsp;
          <span className="projects-dashboard-v2-modal__body__message__strong">
            {teamName}
          </span>
          . Are you sure?
        </div>
        {selectedProjects !== 'all' && <ProjectListComponent items={selectedProjects} category="project" />}
        {selectedProjects === 'all' &&
          <LabeledTextInput
            labelVariant="legacy"
            label="Type DELETE to confirm"
            onChange={setBulkDeleteConfirmationText}
            isDisabled={isLoading}
            value={bulkDeleteConfirmationText}
            placeholder="Are you sure?"
          />}
        {errors &&
          <div className="projects-dashboard-v2-modal__body__errors">
            <ErrorAlert status={{ errors }} />
          </div>}
      </Modal.Body>
      <Modal.Footer bsClass="projects-dashboard-v2-modal__footer">
        <ButtonGroup>
          <Button
            type={BUTTON_TYPE.SECONDARY}
            size={BUTTON_SIZE.MEDIUM}
            isDisabled={isLoading}
            onClick={onHide}
          >
            Cancel
          </Button>
          <InlineLoaderButton
            type={BUTTON_TYPE.PRIMARY}
            size={BUTTON_SIZE.MEDIUM}
            isSubmitButton
            isLoading={isLoading}
            isDisabled={isRemoveButtonDisabled}
            onClick={async () => {
              // The RBAC check for teamId is kinda slow, so we need to pretend we've submitted the data before actually sending it.
              // This way, we can dispatch the proper refetchProjects after we've completed removing the teams.
              dispatch(ProjectsV2Store.actions.preFetchLoading());
              onHide();
              await dispatch(ProjectsV2Store.actions.removeSelectedProjectsFromTeam());
              dispatch(ProjectsV2Store.actions.refetchProjects());
              addToast({ type: 'success', content: 'Successfully removed from team.' });
            }}
          >
            Remove Projects
          </InlineLoaderButton>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};
